import React from 'react';
import { Card, Button, Alert, Stack } from 'react-bootstrap';
import { Formik, Form, FormikHelpers, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isNil } from 'lodash';
import { object } from 'yup';
import { Link } from 'react-router-dom';

import { UserAvatar } from '@components/Feature/User/user-avatar';
import { FormGroupControl } from '@components/Base';
import { ServerError } from '@components/Helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { yupMobileNumber } from '@utils/yup';

import {
  otpCodeSignIn,
  OtpCodeSignInPayload,
  passwordSignIn,
  PasswordSignInPayload,
  camDxSignIn,
} from '../action';
import { selectAuthRouteReducer } from '../selector';
import { routeAuthMemberIdPage } from '../route';

export enum SubmitTypes {
  OtpCode = 'OtpCode',
  Password = 'Password',
}

export type FormValues = { mobileNumber: string; submitType: SubmitTypes | '' };

const FormForm = () => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectAuthRouteReducer);
  const { errors, values, isSubmitting, setFieldValue } = useFormikContext<FormValues>();

  const shouldButtonBeDisabled =
    isSubmitting || !isNil(errors.mobileNumber) || values.mobileNumber === '';

  return (
    <Form>
      <FormGroupControl
        type="tel"
        name="mobileNumber"
        label={t('registration.mobileNumberLabel')}
        groupProps={{ className: 'mb-3' }}
      />

      <Stack gap={2}>
        <Button
          type="submit"
          disabled={shouldButtonBeDisabled}
          onClick={() => setFieldValue('submitType', SubmitTypes.OtpCode)}
        >
          {t('registration.ctaOtp')}
        </Button>

        <Button
          type="submit"
          disabled={shouldButtonBeDisabled}
          onClick={() => setFieldValue('submitType', SubmitTypes.Password)}
          variant="outline-dark"
        >
          {t('registration.ctaPassword')}
        </Button>
      </Stack>
      <ServerError errors={serverErrors} />
    </Form >
  );
}

const AuthMobileNumberPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useSelector(selectAuthRouteReducer);

  const validationSchema = object({
    mobileNumber: yupMobileNumber(),
  });

  const initialValues: FormValues = {
    mobileNumber: '',
    submitType: '',
  };

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const { submitType, ...rest } = values;
    switch (submitType) {
      case SubmitTypes.OtpCode:
        const otpCodeSignInPayload: OtpCodeSignInPayload = rest;
        dispatch(otpCodeSignIn(otpCodeSignInPayload));
        break;
      case SubmitTypes.Password:
        const passwordSignInPayload: PasswordSignInPayload = rest;
        dispatch(passwordSignIn(passwordSignInPayload));
        break;
      default:
        break;
    }
    setSubmitting(false);
  };

  return (
    <Card>
      <Card.Header className="text-center">
        <UserAvatar />
      </Card.Header>
      <Card.Body>
        <Card.Title>{t('registration.title')}</Card.Title>
        <Card.Text>{t('registration.desc')}</Card.Text>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange
          onSubmit={handleSubmit}
        >
          <FormForm />
        </Formik>

        <Card.Subtitle className="my-3 text-center">
          <small className="text-muted">{t('registration.continueWith')}</small>
        </Card.Subtitle>

        <div className="d-grid">
          <Stack direction="horizontal" gap={3} className="mx-auto">
            <Button as={Link} to={routeAuthMemberIdPage()} variant="outline-primary">
              <FontAwesomeIcon icon={faUser} className="me-2" />
              {t('registration.ctaMemberId')}
            </Button>

            <div className="vr" />

            <Button
              onClick={() => dispatch(camDxSignIn())}
              variant="outline-primary"
            >
              <FontAwesomeIcon icon={faKey} className="me-2" />
              {t('registration.ctaCamDx')}
            </Button>
          </Stack>
        </div>

        {error && <Alert className="mt-3" variant="danger">{error}</Alert>}
      </Card.Body>
    </Card>
  );
};

export { AuthMobileNumberPage };
